import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCrud } from '../../lib/hooks';
import {
    SttFormText,
    SttDialog,
    SttFormTextarea,
    SttFormCheck,
} from '../../sporttia/all';

type Consent = {
    id?: number;
    name?: string;
    text?: string;
    textShort?: string;
    forBooking?: boolean;
};

type ConsentDialogProps = {
    item: Consent | null;
    onClose: () => void;
    /** Sin `onSave` el consentimiento se ve en sólo lectura, sin guardar. */
    onSave?: (consent: Consent | null) => void;
};

export default function ConsentDialog({
    item,
    onClose,
    onSave,
}: ConsentDialogProps): React.ReactElement {
    const { t } = useTranslation();

    const [consent, setConsent, setPropertyRaw] = useCrud<Consent>(
        {} as Consent,
    );
    const [nameError, setNameError] = useState(false);

    // Wrapper to adapt useCrud setProperty to form component onChange signature
    const setProperty = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setPropertyRaw({
            name: name as keyof Consent,
            value: value as Consent[keyof Consent],
        });
    };

    useEffect(() => {
        if (item) {
            setConsent(item);
        }
    }, [item, setConsent]);

    if (!item) {
        return <></>;
    }

    return (
        <SttDialog
            open={true}
            onClose={onClose}
            title={
                consent && consent.id ? t('Consent') : t('newConsentTemplate')
            }
            content={
                <Box>
                    <Box mb={3}>
                        <SttFormText
                            caption={t('Name')}
                            name="name"
                            defVal={consent?.name ?? ''}
                            onChange={setProperty}
                            required
                            validation={[
                                {
                                    regex: /^.+$/,
                                    error: t('validation.required'),
                                },
                            ]}
                            setValidationError={setNameError}
                        />
                    </Box>
                    <Box mb={3}>
                        <SttFormTextarea
                            caption={t('text')}
                            name="text"
                            rows={15}
                            defVal={consent?.text ?? ''}
                            onChange={setProperty}
                        />
                    </Box>
                    <Box>
                        <SttFormTextarea
                            caption={t('textShort')}
                            name="textShort"
                            rows={10}
                            defVal={consent?.textShort ?? ''}
                            onChange={setProperty}
                        />
                    </Box>
                    <Box>
                        <SttFormCheck
                            name="forBooking"
                            caption={t('consents.for.bookings')}
                            checked={consent?.forBooking ?? false}
                            onChange={setProperty}
                        />
                    </Box>
                </Box>
            }
            buttons={[
                {
                    type: 'save',
                    show: !!onSave,
                    onClick: () => onSave?.(consent),
                    disabled: nameError,
                },
            ]}
        />
    );
}
